import { useRecoilValue } from 'recoil';
import { EntityTranslation } from '../../domain/EntityTranslation';
import { entityTranslationsState } from '../../atoms/entity-translation/entity-translation.atom';
import { useMemo } from 'react';

export const translateEntity = (
  translations: EntityTranslation[],
  entityId: number,
  entityName: string,
  name: string,
  defaultValue: string,
) => {
  return (
    translations.find(
      (translation) =>
        translation.entityName === entityName &&
        translation.entityId === entityId &&
        translation.name === name,
    )?.value ?? defaultValue
  );
};

interface NestedKeyConfig<T> {
  key: keyof T;
  nestedType: string;
  nestedAttributes: string[];
}

function translateNestedObject<U extends Record<string, any>>(
  nestedObj: U,
  parentKey: string,
  nestedType: string,
  nestedAttributes: string[],
  fallbackId: number | undefined,
  translations: EntityTranslation[],
): U {
  const entityId = fallbackId !== undefined ? fallbackId : nestedObj.id;
  const result = { ...nestedObj };

  nestedAttributes.forEach((attr) => {
    const lookupKey = `${parentKey}.${attr}`;
    const defaultValue = nestedObj[attr];
    const translatedValue = translateEntity(
      translations,
      entityId,
      nestedType,
      lookupKey,
      defaultValue,
    );
    (result as any)[attr] = translatedValue;
  });

  return result;
}

function translateEntityWithNested<U extends Record<string, any>>(
  entity: U,
  currentEntityType: string,
  currentAttributes: (keyof U)[],
  currentNestedKeys:
    | { key: keyof U; nestedType: string; nestedAttributes: string[] }[]
    | undefined,
  fallbackId: number | undefined,
  translations: EntityTranslation[],
): U {
  const entityId = fallbackId !== undefined ? fallbackId : entity.id;

  let translatedEntity = currentAttributes.reduce(
    (acc, attr) => ({
      ...acc,
      [attr]: translateEntity(
        translations,
        entityId,
        currentEntityType,
        attr as string,
        (entity as any)[attr],
      ) as U[typeof attr],
    }),
    { ...entity },
  );

  if (currentNestedKeys) {
    currentNestedKeys.forEach(({ key, nestedType, nestedAttributes }) => {
      const nestedValue = entity[key];
      if (Array.isArray(nestedValue)) {
        translatedEntity = {
          ...translatedEntity,
          [key]: nestedValue.map((nestedItem: any) =>
            translateEntityWithNested(
              nestedItem,
              nestedType,
              nestedAttributes,
              currentNestedKeys,
              undefined,
              translations,
            ),
          ),
        };
      } else if (nestedValue && typeof nestedValue === 'object') {
        translatedEntity = {
          ...translatedEntity,
          [key]: translateNestedObject(
            nestedValue,
            String(key),
            nestedType,
            nestedAttributes,
            entityId,
            translations,
          ),
        };
      }
    });
  }

  return translatedEntity;
}

export function useTranslateEntity<
  T extends Record<string, any>,
  K extends keyof T,
>(
  entity: T | undefined | null,
  entityType: string,
  attributes: K[],
  nestedKeys?: NestedKeyConfig<T>[],
): T | undefined | null {
  const entityTranslations = useRecoilValue(entityTranslationsState);

  return useMemo(() => {
    if (!entity) {
      return entity;
    }

    return translateEntityWithNested(
      entity,
      entityType,
      attributes,
      nestedKeys as
        | { key: keyof T; nestedType: string; nestedAttributes: string[] }[]
        | undefined,
      undefined,
      entityTranslations,
    );
  }, [entity, entityTranslations, entityType, attributes, nestedKeys]);
}

export function useTranslateEntityArray<
  T extends Record<string, any>,
  K extends keyof T,
>(
  entities: T[],
  entityType: string,
  attributes: K[],
  nestedKeys?: NestedKeyConfig<T>[],
): T[] {
  const entityTranslations = useRecoilValue(entityTranslationsState);

  return useMemo(
    () =>
      entities.map((entity) =>
        translateEntityWithNested(
          entity,
          entityType,
          attributes,
          nestedKeys as
            | { key: keyof T; nestedType: string; nestedAttributes: string[] }[]
            | undefined,
          undefined,
          entityTranslations,
        ),
      ),
    [entities, entityTranslations, entityType, attributes, nestedKeys],
  );
}
